import { randomUUID } from 'client/utils';
import { createContext, useEffect, useMemo, useState } from 'react';
import calApi from '../api/calApi';
import {
    Booking,
    CalBookingProps,
    CalEventData,
    CalEventDataWithDisplayPrice,
    EventServerModel,
    WidgetStep,
} from '../types';
import { resolveCalProviderLanguage, resolveSiteLocale } from '../utils';

// State interface
export interface RuntimeCalBookingState {
    isLoading: boolean;
    confirmButtonDisabled: boolean;
    bookerKey: string | null;
    eventSlug: string | null;
    rescheduleUid: string | null;
    cancelBookingId: string | null;
    confirmationData: Booking | null;
    calEventData: CalEventData | null;
    teamId: number | null;
    userName: string | null;
    language: string;
    /** The site page's raw Duda locale — what the native booker and its emails localize to. */
    siteLocale: string;
    apiUrl: string;
    eventSelection: CalEventDataWithDisplayPrice | null;
    selectedHostId: number | null;
    widgetEditorStep?: WidgetStep | null;
}

export interface RuntimeCalBookingActions {
    setIsLoading: (isLoading: boolean) => void;
    setConfirmButtonDisabled: (confirmButtonDisabled: boolean) => void;
    resetBookerState: () => void;
    setRescheduleUid: (rescheduleUid: string | null) => void;
    setCancelBookingId: (cancelBookingId: string | null) => void;
    setConfirmationData: (confirmationData: Booking | null) => void;
    clearConfirmationData: () => void;
    onBookingSuccess: (booking: Booking) => void;
    setCalEventData: (calEventData: CalEventData) => void;
    setEventSlug: (eventSlug: string | null) => void;
    setTeamId: (teamId: number | null) => void;
    setUserName: (userName: string | null) => void;
    setEventSelection: (event: CalEventDataWithDisplayPrice | null) => void;
    setSelectedHostId: (selectedHostId: number | null) => void;
    resetEditorStep: () => void;
}

// Initial state
export const initialState: RuntimeCalBookingState = {
    isLoading: true,
    confirmButtonDisabled: false,
    bookerKey: randomUUID(),
    eventSlug: null,
    rescheduleUid: null,
    cancelBookingId: null,
    confirmationData: null,
    calEventData: null,
    teamId: null,
    userName: null,
    language: 'en',
    siteLocale: 'en',
    apiUrl: calApi.apiUrl,
    eventSelection: null,
    selectedHostId: null,
    widgetEditorStep: null,
};

export const useRuntimeCalBookingState = (props: CalBookingProps) => {
    const [state, setState] = useState<RuntimeCalBookingState>(() => {
        calApi.setCalDomain(props.calApiDomain);
        calApi.setApiBaseUrl(props.calApiBaseUrl);
        calApi.setCalClientId(props.clientId);

        const eventSlug =
            props.events && props.events.length === 1
                ? // Backward compatibility with single event booking
                  props.events?.[0]?.slug || props.eventSlug || null
                : null;

        return {
            ...initialState,
            language: resolveCalProviderLanguage(),
            siteLocale: resolveSiteLocale(),
            apiUrl: calApi.apiUrl,
            teamId: props.teamId,
            userName: props.userName,
            eventSlug,
            selectedHostId: null,
            widgetEditorStep: props.editorStep || null,
        };
    });

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            widgetEditorStep: props.editorStep || null,
        }));
    }, [props.editorStep]);

    const actions = useMemo(
        () =>
            ({
                setIsLoading: (isLoading: boolean) =>
                    setState((prev) => ({ ...prev, isLoading })),

                setConfirmButtonDisabled: (confirmButtonDisabled: boolean) =>
                    setState((prev) => ({ ...prev, confirmButtonDisabled })),

                resetBookerState: () =>
                    setState((prev) => ({ ...prev, bookerKey: randomUUID() })),

                setRescheduleUid: (rescheduleUid: string | null) =>
                    setState((prev) => ({ ...prev, rescheduleUid })),

                setCancelBookingId: (cancelBookingId: string | null) =>
                    setState((prev) => ({ ...prev, cancelBookingId })),

                setEventSlug: (eventSlug: string | null) =>
                    setState((prev) => ({ ...prev, eventSlug })),

                setConfirmationData: (confirmationData: Booking | null) =>
                    setState((prev) => ({ ...prev, confirmationData })),
                clearConfirmationData: () =>
                    setState((prev) => ({
                        ...prev,
                        confirmationData: null,
                        bookerKey: randomUUID(),
                    })),
                onBookingSuccess: (booking: Booking) =>
                    setState((prev) => ({
                        ...prev,
                        rescheduleUid: null,
                        confirmationData: booking,
                    })),
                setEvent: (event: EventServerModel) =>
                    setState((prev) => ({ ...prev, event })),

                setCalEventData: (calEventData: CalEventData) =>
                    setState((prev) => ({ ...prev, calEventData })),
                setTeamId: (teamId: number | null) =>
                    setState((prev) => ({ ...prev, teamId })),
                setUserName: (userName: string | null) =>
                    setState((prev) => ({ ...prev, userName })),
                setEventSelection: (
                    eventSelection: CalEventDataWithDisplayPrice | null
                ) =>
                    setState((prev) => ({
                        ...prev,
                        eventSelection,
                    })),
                setSelectedHostId: (selectedHostId: number | null) => {
                    setState((prev) => ({
                        ...prev,
                        selectedHostId,
                    }));
                },
                resetEditorStep: () => {
                    setState((prev) => ({ ...prev, widgetEditorStep: null }));
                },
            } as RuntimeCalBookingActions),
        []
    );

    return [state, actions] as const;
};

export const BookerStateContext = createContext<
    [RuntimeCalBookingState, RuntimeCalBookingActions]
>([
    initialState,
    {
        setIsLoading: () => {},
        setConfirmButtonDisabled: () => {},
        resetBookerState: () => {},
        setRescheduleUid: () => {},
        setCancelBookingId: () => {},
        setEventSlug: () => {},
        setConfirmationData: () => {},
        clearConfirmationData: () => {},
        onBookingSuccess: () => {},
        setCalEventData: () => {},
        setTeamId: () => {},
        setUserName: () => {},
        setEventSelection: () => {},
        setSelectedHostId: () => {},
        resetEditorStep: () => {},
    } satisfies RuntimeCalBookingActions,
]);
